import { get, set } from 'lodash-es';
import { Schema } from 'zod';
import { logError } from '../validateSchema';

/**
 * validateSettingsScheme function accepts a validation schema and data to validate against that schema.
 *
 * The function first attempts to validate the provided data using a safe parsing method.
 * If the data is valid, the result is returned immediately.
 *
 * If validation fails, the function then:
 *   1. Generates a default data object by parsing an empty object with the schema,
 *      which produces an object with the default values as defined by the schema.
 *   2. Creates a shallow copy of the original data (patchedData) that will be modified.
 *   3. Iterates over each error from the initial validation:
 *      - For errors with a specific path, retrieves the default value from the default data.
 *      - Patches the copied data by setting the default value at the error's path.
 *      - Logs the error with the tag 'SettingsValidation'.
 *   4. Re-validates the patched data and returns the result.
 *
 * @template T - The type of the data being validated.
 * @param {Schema} schema - The validation schema (Zod schema).
 * @param {T} data - The data object to be validated.
 *
 * Note: Errors occur only when the data violates the settings scheme rules, as the scheme does not allow required fields.
 */

const findDefaultValue = (data: object, path: (string | number)[]) => {
  if (!path.length) {
    return [];
  }

  const value = get(data, path);

  if (value) {
    return [value, path];
  }
  return findDefaultValue(
    data,
    path?.length ? path.slice(0, path.length - 1) : []
  );
};

export const validateSettingsScheme = <T>(schema: Schema, data: T) => {
  const result = schema.safeParse(data);

  if (result.success) {
    return result;
  }

  const defaultData = schema.parse({});

  const patchedData = { ...data } as object;

  result.error.errors.forEach((err) => {
    if (err.path.length) {
      const defaultValue = get(defaultData, err.path);
      if (!defaultValue) {
        const [deepDefaultValue, newPath] = findDefaultValue(
          defaultData,
          err.path
        );
        set(patchedData, newPath, deepDefaultValue);
      } else {
        set(patchedData, err.path, defaultValue);
      }
      logError(err, 'SettingsValidation');
    }
  });

  return schema.safeParse(patchedData);
};
